import React, { useEffect } from 'react';
import { Card, Form, Select, Typography, theme } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { normalizeTransportAddressList } from '../utils/transportAddressUtils';
import { resolveOrderDeliveryAddress } from '../utils/orderDeliveryAddress';
import './TransportOrderRoutePointsSection.css';

interface OrderRouteOption {
  id: string;
  orderNumber: string;
  displayOrderNumber?: string;
  customerName?: string;
  factoryLocation?: string | null;
  deliveryLocation?: string | null;
  customer?: { address?: string | null } | null;
}

export default function TransportOrderRoutePointsSection({ form, orders }: { form: FormInstance; orders: OrderRouteOption[] }) {
  const { t } = useTranslation('transport');
  const { token } = theme.useToken();
  const loading = normalizeTransportAddressList(Form.useWatch('loadingAddresses', { form, preserve: true }) ?? form.getFieldValue('loadingAddresses'));
  const delivery = normalizeTransportAddressList(Form.useWatch('deliveryAddresses', { form, preserve: true }) ?? form.getFieldValue('deliveryAddresses'));
  const signature = JSON.stringify({ loading, delivery, orders });
  useEffect(() => {
    const source = JSON.parse(signature) as { loading: string[]; delivery: string[]; orders: OrderRouteOption[] };
    for (const order of source.orders) {
      for (const [field, addresses, hint] of [
        ['loadingAddress', source.loading, order.factoryLocation],
        ['deliveryAddress', source.delivery, resolveOrderDeliveryAddress(order)],
      ] as const) {
        const path = ['orderRoutePointsByOrder', order.id, field];
        const current = form.getFieldValue(path);
        if (current && addresses.includes(current)) continue;
        // A single point is unambiguous; otherwise only an exact order-address match.
        const next = addresses.length === 1 ? addresses[0] : hint && addresses.includes(hint.trim()) ? hint.trim() : null;
        if (next !== current) form.setFieldValue(path, next);
      }
    }
  }, [form, signature]);
  if (!orders.length) return null;
  return <Card title={t('orderRoutePoints.title')} className="transport-order-points" style={{
    '--route-point-line': token.colorBorderSecondary,
    '--route-point-surface': token.colorFillAlter,
    '--route-point-accent': token.colorPrimary,
  } as React.CSSProperties}>
    <Typography.Paragraph type="secondary">{t('orderRoutePoints.hint')}</Typography.Paragraph>
    {orders.map(order => <div key={order.id} className="transport-order-points__order">
      <div className="transport-order-points__identity">
        <Typography.Text strong>{order.displayOrderNumber || order.orderNumber}</Typography.Text>
        {order.customerName && <Typography.Text type="secondary">{order.customerName}</Typography.Text>}
      </div>
      <div className="transport-order-points__fields">
        {([['loadingAddress', loading], ['deliveryAddress', delivery]] as const).map(([field, addresses]) =>
          <Form.Item key={field} className={`transport-order-points__${field}`} name={['orderRoutePointsByOrder', order.id, field]} label={t(`orderRoutePoints.${field}`)}
            rules={[{ validator: (_, value) => addresses.length === 0 || addresses.includes(value) ? Promise.resolve() : Promise.reject(new Error(t('orderRoutePoints.required'))) }]}>
            <Select aria-label={`${order.displayOrderNumber || order.orderNumber}: ${t(`orderRoutePoints.${field}`)}`}
              placeholder={t('orderRoutePoints.choose')} disabled={!addresses.length}
              options={addresses.map(address => ({ value: address, label: address.replace(/\n/g, ', ') }))} />
          </Form.Item>)}
        <ArrowRightOutlined className="transport-order-points__arrow" aria-hidden="true" />
      </div>
    </div>)}
  </Card>;
}
